import './Button.scss';

import React, { DOMAttributes, forwardRef, MutableRefObject, ReactNode } from 'react';
import { CircularProgress } from '@novo-electronique/react-circular-progress';
import classNames from 'classnames';

export enum ButtonStyle {
  Normal = 'normal',
  Primary = 'primary',
  Action = 'action',
  Danger = 'danger',
  Stealth = 'stealth',
  Link = 'link',
}

export enum ButtonSize {
  Normal = 'normal',
  Small = 'small',
}

type StyleType<T extends string> = T | ButtonStyle;
type SizeType<T extends string> = T | ButtonSize;

interface IProps<Style extends string, Size extends string> extends DOMAttributes<HTMLButtonElement> {
  children: string | ReactNode | ReactNode[];
  style?: StyleType<Style>;
  size?: SizeType<Size>;
  type?: 'button' | 'submit' | 'reset';
  isLoading?: boolean;
  disabled?: boolean;
}

function Button<Style extends string, Size extends string>(
  {
    children,
    style = ButtonStyle.Normal,
    size = ButtonSize.Normal,
    type = 'button',
    isLoading = false,
    disabled = false,
    ...props
  }: IProps<Style, Size>,
  ref,
) {
  const className = classNames('novoButton', {
    [`novoButton--${style}`]: Boolean(style),
    [`novoButton--${size}`]: Boolean(size),
    'novoButton--is-loading': isLoading,
  });

  return (
    <button ref={ref} {...props} className={className} type={type} disabled={isLoading || disabled}>
      {isLoading && <CircularProgress />}
      {children}
    </button>
  );
}

export default forwardRef(Button) as <Style extends string, Size extends string>(
  props: IProps<Style, Size> & { ref?: MutableRefObject<HTMLButtonElement> },
) => ReturnType<typeof Button>;
